import React, { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import './LoginPage.css';

function RegisterPage({ onBackToLogin }) {
  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      // Create the real Firebase Auth account — Firebase itself handles
      // storing the password securely, we never see or store it ourselves.
      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);

      // Create the matching Firestore profile. Note the document ID is the
      // Firebase Auth UID — this is required for the security rules to be
      // able to look up "who is this person" safely.
      await setDoc(doc(db, 'users', credential.user.uid), {
        name: name.trim(),
        email: email.trim(),
        role: null,               // assigned by an admin at approval time
        approvalStatus: 'pending',
        active: false,
        createdAt: serverTimestamp()
      });

      // App.js is listening for auth state changes and will automatically
      // move to the "pending approval" screen once this resolves.
      setSubmitted(true);

    } catch (err) {
      console.error('Registration error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('An account already exists for this email. Try logging in instead.');
      } else if (err.code === 'auth/invalid-email') {
        setError('That email address doesn\u2019t look valid.');
      } else {
        setError('Something went wrong. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="login-page">

      <div className="login-background">
        <div className="grain-overlay"></div>
        <div className="corporate-glow"></div>
        <div className="grain-line line-1"></div>
        <div className="grain-line line-2"></div>
        <div className="grain-line line-3"></div>
      </div>

      <div className="grain-silhouettes">
        <div className="silhouette silhouette-1"></div>
        <div className="silhouette silhouette-2"></div>
        <div className="silhouette silhouette-3"></div>
        <div className="silhouette silhouette-4"></div>
      </div>

      <div className="login-container">

        <div className="login-logo">
          <div className="logo-container">
            <h1 className="dilex-wordmark">dilex</h1>
            <div className="grain-accent-line"></div>
          </div>
          <p className="login-subtitle">Operations Management System</p>
        </div>

        {submitted ? (
          <div className="status-card">
            <div className="status-icon">&#128231;</div>
            <h2 className="status-title">Request Submitted</h2>
            <p className="status-message">
              Thanks, {name.split(' ')[0]}. Your access request has been sent
              to an administrator for approval. You'll be able to sign in as
              soon as it's approved &mdash; try logging in again in a little while.
            </p>
            <button type="button" className="login-button" onClick={onBackToLogin}>
              Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            <h2>Request Access</h2>

            <div className="input-container">
              <span className="input-icon">&#128100;</span>
              <input
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="text-input"
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="input-container">
              <span className="input-icon">&#9993;&#65039;</span>
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="text-input"
                disabled={loading}
              />
            </div>

            <div className="input-container">
              <span className="input-icon">&#128272;</span>
              <input
                type="password"
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="text-input"
                disabled={loading}
              />
            </div>

            <div className="input-container">
              <span className="input-icon">&#128272;</span>
              <input
                type="password"
                placeholder="Confirm password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="text-input"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="error-message">
                <span>&#9888;&#65039;</span> {error}
              </div>
            )}

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? (
                <>
                  <span className="button-spinner"></span>
                  Submitting...
                </>
              ) : (
                'REQUEST ACCESS'
              )}
            </button>

            <div className="login-links" style={{ textAlign: 'center' }}>
              <a href="#" className="forgot-link" onClick={(e) => { e.preventDefault(); onBackToLogin(); }}>
                Already have access? Log in
              </a>
            </div>
          </form>
        )}

        <div className="login-footer">
          <div className="footer-divider"></div>
          <div className="footer-micro">
            <span className="version-text">Powered by DILEX Holdings</span>
          </div>
        </div>

      </div>
    </div>
  );
}

export default RegisterPage;
